import axios from 'axios';
import { BeerXchangeCollection } from '../types/model/beerXchangeCollection';
import { Beer } from '../types/model/beer';
import { Brewery } from '../types/model/brewery';
import { BeerXchangeUser } from '../types/model/beerXchangeUser';
import { BeerWithClaims } from '../types/model/beerWithClaims';
import { Trade } from '../types/model/trade';
import { UserEvents } from '../types/model/userEvents';
import { Seeking } from '../types/model/seeking';
import { Offer } from '../types/model/offer';
import { Shipping } from '../types/model/shipping';
import { Rating } from '../types/model/rating';
import { Price } from '../types/model/price';
import { DirectoryIndex, DirectoryPage } from '../types/model/directory';

//const BEERXCHANGE_BASE_URL = "http://127.0.0.1:8000/v1/";
const BEERXCHANGE_BASE_URL = 'https://api.beerxchange.com/v1/';

//const BEERXCHANGE_BASE_URL2 = 'http://127.0.0.1:8000/v1/';
const BEERXCHANGE_BASE_URL2 = 'https://api2.beerxchange.com/v1/';

const BEERS_URL = `${BEERXCHANGE_BASE_URL}beers`;
const BREWERIES_URL = `${BEERXCHANGE_BASE_URL}breweries`;
const COLLECTION_URL = `${BEERXCHANGE_BASE_URL}collections`;
const CLAIMS_URL = `${BEERXCHANGE_BASE_URL}claims`;
const USERS_URL = `${BEERXCHANGE_BASE_URL}users`;
const TRADES_URL = `${BEERXCHANGE_BASE_URL}trades`;
const USEREVENTS_URL = `${BEERXCHANGE_BASE_URL}events`;
const RATINGS_URL = `${BEERXCHANGE_BASE_URL}ratings`;
const SEEKING_URL = `${BEERXCHANGE_BASE_URL}seeking`;
const OFFERING_URL = `${BEERXCHANGE_BASE_URL}offering`;
const MATCHING_URL = `${BEERXCHANGE_BASE_URL}matching`;
const OFFERS_URL = `${BEERXCHANGE_BASE_URL}offers`;
const PRICES_URL = `${BEERXCHANGE_BASE_URL}prices`;
const DIRECTORIES_URL = `${BEERXCHANGE_BASE_URL2}directories`;

/**
 * Accesses the beerxchange api. For documentation visit https://www.beerxchange.com/api
 */
class BeerXchangeApi {
  headers?: object;

  constructor(access_code?: string, untappdId?: string) {
    this.headers = {
      //authorization: 'Bearer ' + access_code,
      access_token: access_code,
      identity: untappdId,
    };
  }

  async authorizeUser(code: string) {
    const url = `${BEERXCHANGE_BASE_URL}authorize_reactweb/${code}`;
    return axios.get(url).then((response) => response.data);
  }

  buildQueryParams(query: string) {
    return Object.assign(
      {},
      {
        query,
      }
    );
  }

  async getUserInfo(userName: string) {
    const url = `${USERS_URL}/${userName}`;
    return axios
      .get<BeerXchangeUser>(url, {
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async getUntappdUserInfo(userName: string) {
    const url = `${BEERXCHANGE_BASE_URL2}users/${userName}/full`;
    return axios
      .get<BeerXchangeUser>(url, {
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async getCollectionByID(id: string) {
    const url = `${BEERXCHANGE_BASE_URL2}collections/${id}`;
    return axios
      .get<BeerXchangeCollection>(url, {
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async getCollectionForUser(userName: string) {
    const url = `${COLLECTION_URL}/${userName}`;
    return axios
      .get<BeerXchangeCollection>(url, {
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async updateCollection(
    collectionId: string,
    name: string,
    location: string,
    isPublic: boolean,
    showValue: boolean,
    defaultCurrency: string,
    showConsumed: boolean
  ) {
    var buildUrl = `${COLLECTION_URL}/${collectionId}`;

    if (name !== null) {
      buildUrl = buildUrl.concat('?name=', encodeURIComponent(name));
    }

    if (location !== null) {
      buildUrl = buildUrl.concat('&location=', encodeURIComponent(location));
    }

    if (isPublic !== null) {
      buildUrl = buildUrl.concat('&isPublic=', isPublic.toString());
    }

    if (showValue !== null) {
      buildUrl = buildUrl.concat('&showValue=', showValue.toString());
    }

    if (defaultCurrency !== null) {
      buildUrl = buildUrl.concat('&defaultCurrency=', defaultCurrency);
    }

    if (showConsumed !== null) {
      buildUrl = buildUrl.concat('&showConsumed=', showConsumed.toString());
    }

    return axios
      .post(
        buildUrl,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async getClaimsForUser(userName: string) {
    const url = `${CLAIMS_URL}/${userName}`;
    return axios
      .get<BeerWithClaims[]>(url, {
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async getConsumedClaimsForUser(userName: string) {
    const url = `${CLAIMS_URL}/${userName}/consumed`;
    return axios
      .get<BeerWithClaims[]>(url, {
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async searchBeers(query: string) {
    return axios
      .get<Beer[]>(BEERS_URL, {
        params: this.buildQueryParams(query),
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async searchBreweries(query: string) {
    return axios
      .get<Brewery[]>(BREWERIES_URL, {
        params: this.buildQueryParams(query),
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async getBrewery(breweryUntappdId: string) {
    return axios
      .get<Brewery>(`${BREWERIES_URL}/${breweryUntappdId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getBreweryDetails(breweryUntappdId: string) {
    return axios
      .get<Brewery>(`${BREWERIES_URL}/${breweryUntappdId}/full`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getBreweryBeers(breweryUntappdId: string) {
    return axios
      .get<Beer[]>(`${BREWERIES_URL}/${breweryUntappdId}/list`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getBeer(beerUntappdId: string) {
    return axios
      .get<Beer>(`${BEERS_URL}/${beerUntappdId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.log(error);
      });
  }

  async getBeerDetails(beerUntappdId: string) {
    return axios
      .get<Beer>(`${BEERS_URL}/${beerUntappdId}/full`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async claimBeer(beerUntappdId: string) {
    return axios
      .post<Beer>(
        `${CLAIMS_URL}/${beerUntappdId}`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async editClaimedBeer(
    claimedBeerId: string,
    count: string,
    denomination: string,
    price: string,
    size: string,
    bottled: string,
    added: string,
    drink_by: string,
    notes: string,
    location: string,
    purchased: string
  ) {
    var buildUrl = `${CLAIMS_URL}/${claimedBeerId}/edit?`;

    if (count !== null && count !== undefined) {
      buildUrl = buildUrl.concat('count=', count);
    }
    if (denomination !== null && denomination !== undefined) {
      buildUrl = buildUrl.concat('&denomination=', denomination);
    }
    if (price !== null && price !== undefined) {
      buildUrl = buildUrl.concat('&price=', price);
    }
    if (size !== null && size !== undefined) {
      buildUrl = buildUrl.concat('&size=', size);
    }
    if (bottled !== null && !Number.isInteger(bottled)) {
      bottled = bottled.replaceAll('/', '-');
      buildUrl = buildUrl.concat('&bottled=', bottled);
    }
    if (added !== null && !Number.isInteger(added)) {
      added = added.replaceAll('/', '-');
      buildUrl = buildUrl.concat('&added=', added);
    }
    if (drink_by !== null && !Number.isInteger(drink_by)) {
      drink_by = drink_by.replaceAll('/', '-');
      buildUrl = buildUrl.concat('&drink_by=', drink_by);
    }
    if (notes !== null && notes !== undefined) {
      buildUrl = buildUrl.concat('&notes=', encodeURIComponent(notes));
    }
    if (location !== null && location !== undefined) {
      buildUrl = buildUrl.concat('&location=', encodeURIComponent(location));
    }
    if (purchased !== null && purchased !== undefined) {
      buildUrl = buildUrl.concat('&purchased=', encodeURIComponent(purchased));
    }

    return axios
      .post(
        buildUrl,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async removeBeer(claimedBeerId: string) {
    return axios.delete(`${CLAIMS_URL}/${claimedBeerId}`, {
      headers: this.headers,
    });
  }

  async drinkBeer(claimedBeerId: string) {
    return axios
      .post(
        `${CLAIMS_URL}/${claimedBeerId}/drink?count=1`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async undrinkBeer(claimedBeerId: string) {
    return axios
      .post(
        `${CLAIMS_URL}/${claimedBeerId}/undrink?count=1`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async listUsers() {
    return axios
      .get<BeerXchangeUser[]>(USERS_URL, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getTrade(tradeId: string) {
    return axios
      .get<Trade>(`${TRADES_URL}/${tradeId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async listTrades(userId: string) {
    return axios
      .get<Trade[]>(`${TRADES_URL}/${userId}/current`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async listTradeHistory(userId: string) {
    return axios
      .get<Trade[]>(`${TRADES_URL}/${userId}/history`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getUserSeeking(userId: string) {
    const url = `${SEEKING_URL}Lists/${userId}`;
    return axios
      .get<Seeking[]>(url, {
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async getUserOffering(userId: string) {
    const url = `${OFFERING_URL}Lists/${userId}`;
    return axios
      .get<Offer[]>(url, {
        headers: this.headers,
      })
      .then((response) => response.data);
  }

  async seekBeer(beerUntappdId: string) {
    return axios
      .post<Beer>(
        `${SEEKING_URL}Beers/${beerUntappdId}`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async unseekBeer(beerUntappdId: string) {
    return axios
      .delete(`${SEEKING_URL}Beers/${beerUntappdId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async seekBrewery(breweryUntappdId: string) {
    return axios
      .post<Brewery>(
        `${SEEKING_URL}Breweries/${breweryUntappdId}`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async unseekBrewery(breweryUntappdId: string) {
    return axios
      .delete(`${SEEKING_URL}Breweries/${breweryUntappdId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async offerBeer(beerUntappdId: string) {
    return axios
      .post<Beer>(
        `${OFFERING_URL}Beers/${beerUntappdId}`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async unofferBeer(beerUntappdId: string) {
    return axios
      .delete(`${OFFERING_URL}Beers/${beerUntappdId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async offerBrewery(breweryUntappdId: string) {
    return axios
      .post<Brewery>(
        `${OFFERING_URL}Breweries/${breweryUntappdId}`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async unofferBrewery(breweryUntappdId: string) {
    return axios
      .delete(`${OFFERING_URL}Breweries/${breweryUntappdId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async acceptOffer(offerId: string, notes: string) {
    return axios
      .post<Offer>(
        `${OFFERS_URL}/${offerId}/accept?notes=` + encodeURIComponent(notes),
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async cancelOffer(offerId: string, notes: string) {
    return axios
      .post<Offer>(
        `${OFFERS_URL}/${offerId}/cancel?notes=` + encodeURIComponent(notes),
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async rejectOffer(offerId: string, notes: string) {
    return axios
      .post<Offer>(
        `${OFFERS_URL}/${offerId}/reject?notes=` + encodeURIComponent(notes),
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async beginShipping(
    offerId: string,
    shippingType: string,
    carrier: string,
    trackingNumber: string,
    shippingCost: string,
    notes: string
  ) {
    var buildUrl = `${OFFERS_URL}/${offerId}/ship?`;
    if (shippingType !== null) {
      buildUrl = buildUrl.concat('shippingType=', shippingType);
    }
    if (carrier !== null) {
      buildUrl = buildUrl.concat('&carrier=', carrier);
    }
    if (trackingNumber !== null) {
      buildUrl = buildUrl.concat('&trackingNumber=', trackingNumber);
    }
    if (shippingCost !== null) {
      buildUrl = buildUrl.concat('&shippingCost=', shippingCost);
    }
    if (notes !== null) {
      buildUrl = buildUrl.concat('&notes=', encodeURIComponent(notes));
    }
    return axios
      .post<Offer>(
        buildUrl,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async markComplete(
    tradeId: string,
    ratingValue: string,
    timelyShipping: string,
    communicatesWell: string,
    helpful: string,
    wouldTradeAgain: string,
    notes: string
  ) {
    var buildUrl = `${TRADES_URL}/${tradeId}/complete?`;
    if (ratingValue !== null) {
      buildUrl = buildUrl.concat('ratingValue=', ratingValue);
    }
    if (timelyShipping !== null) {
      buildUrl = buildUrl.concat('&timelyShipping=', timelyShipping);
    }
    if (communicatesWell !== null) {
      buildUrl = buildUrl.concat('&communicatesWell=', communicatesWell);
    }
    if (helpful !== null) {
      buildUrl = buildUrl.concat('&helpful=', helpful);
    }
    if (wouldTradeAgain !== null) {
      buildUrl = buildUrl.concat('&wouldTradeAgain=', wouldTradeAgain);
    }
    if (notes !== null) {
      buildUrl = buildUrl.concat('&notes=', encodeURIComponent(notes));
    }
    return axios
      .post<Trade>(
        buildUrl,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async markException(
    tradeId: string,
    ratingValue: string,
    timelyShipping: string,
    communicatesWell: string,
    helpful: string,
    wouldTradeAgain: string,
    notes: string
  ) {
    var buildUrl = `${TRADES_URL}/${tradeId}/exception?`;
    if (ratingValue !== null) {
      buildUrl = buildUrl.concat('ratingValue=', ratingValue);
    }
    if (timelyShipping !== null) {
      buildUrl = buildUrl.concat('&timelyShipping=', timelyShipping);
    }
    if (communicatesWell !== null) {
      buildUrl = buildUrl.concat('&communicatesWell=', communicatesWell);
    }
    if (helpful !== null) {
      buildUrl = buildUrl.concat('&helpful=', helpful);
    }
    if (wouldTradeAgain !== null) {
      buildUrl = buildUrl.concat('&wouldTradeAgain=', wouldTradeAgain);
    }
    if (notes !== null) {
      buildUrl = buildUrl.concat('&notes=', encodeURIComponent(notes));
    }
    return axios
      .post<Trade>(
        buildUrl,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async matchingLists(userId: string) {
    return axios
      .get<Offer>(`${MATCHING_URL}/Lists/${userId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
  async matchingBeers(untappdBeerId: string) {
    return axios
      .get<Offer>(`${MATCHING_URL}Beers/${untappdBeerId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
  async matchingBreweries(untappdBreweryId: string) {
    return axios
      .get<Offer>(`${MATCHING_URL}Breweries/${untappdBreweryId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
  async matchingSeekingBeers(untappdBeerId: string) {
    return axios
      .get<Offer>(`${MATCHING_URL}SeekingBeers/${untappdBeerId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
  async matchingSeekingBreweries(untappdBreweryId: string) {
    return axios
      .get<Offer>(`${MATCHING_URL}SeekingBreweries/${untappdBreweryId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
  async makeOffer(
    userName: string,
    offeredBeers: string[],
    requestedBeers: string[],
    notes: string,
    ttl: string
  ) {
    var buildUrl = `${OFFERS_URL}/${userName}`;
    buildUrl = buildUrl.concat('?tradingBeers=', offeredBeers.toString());
    buildUrl = buildUrl.concat('&selectBeers=', requestedBeers.toString());

    if (notes !== null) {
      buildUrl = buildUrl.concat('&notes=', encodeURIComponent(notes));
    }
    if (ttl !== null) {
      buildUrl = buildUrl.concat('&ttl=', ttl);
    }

    return axios
      .post<Offer>(
        buildUrl,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async updateOffer(
    offerId: string,
    tradeId: string,
    offeredBeers: string[],
    requestedBeers: string[],
    notes: string,
    ttl: string
  ) {
    var buildUrl = `${OFFERS_URL}/${offerId}/trade/${tradeId}`;
    buildUrl = buildUrl.concat('?tradingBeers=', offeredBeers.toString());
    buildUrl = buildUrl.concat('&selectBeers=', requestedBeers.toString());

    if (notes !== null) {
      buildUrl = buildUrl.concat('&notes=', encodeURIComponent(notes));
    }

    if (ttl !== null) {
      buildUrl = buildUrl.concat('&ttl=', ttl);
    }

    return axios
      .post<Offer>(
        buildUrl,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }
  async getUserEvents(userId: string) {
    return axios
      .get<UserEvents>(`${USEREVENTS_URL}/${userId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
  async dismissEvent(eventId: string) {
    return axios
      .post<UserEvents>(
        `${USEREVENTS_URL}/${eventId}`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }
  async undoDismissEvent(eventId: string) {
    return axios
      .post<UserEvents>(
        `${USEREVENTS_URL}/${eventId}/undo`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async getShippingForTrade(tradeId: string) {
    return axios
      .get<Shipping[]>(`${TRADES_URL}/${tradeId}/shipping`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async updateUserInfo(
    userName: string,
    firstName: string,
    lastName: string,
    address: string,
    city: string,
    state: string,
    country: string,
    zipcode: string,
    defaultView: string
  ) {
    var buildUrl = `${USERS_URL}/${userName}`;

    if (firstName !== null) {
      buildUrl = buildUrl.concat('?firstName=', encodeURIComponent(firstName));
    }

    if (lastName !== null) {
      buildUrl = buildUrl.concat('&lastName=', encodeURIComponent(lastName));
    }

    if (address !== null) {
      buildUrl = buildUrl.concat('&address=', encodeURIComponent(address));
    }
    if (city !== null) {
      buildUrl = buildUrl.concat('&city=', encodeURIComponent(city));
    }
    if (state !== null) {
      buildUrl = buildUrl.concat('&state=', encodeURIComponent(state));
    }
    if (country !== null) {
      buildUrl = buildUrl.concat('&country=', encodeURIComponent(country));
    }
    if (zipcode !== null) {
      buildUrl = buildUrl.concat('&zipcode=', encodeURIComponent(zipcode));
    }
    if (defaultView !== null) {
      buildUrl = buildUrl.concat('&defaultView=', encodeURIComponent(defaultView));
    }

    return axios
      .post<BeerXchangeUser>(
        buildUrl,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async exportCollection(collectionId: string) {
    return axios
      .post<BeerXchangeCollection>(
        `${COLLECTION_URL}/${collectionId}/export`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  async importCollection(collectionId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return axios
      .post<any[]>(`${COLLECTION_URL}/${collectionId}/import`, formData, {
        headers: {
          ...this.headers,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        return response.data;
      });
  }
  async refreshCollection(collectionId: string) {
    return axios
      .post<BeerXchangeCollection>(
        `${COLLECTION_URL}/${collectionId}/refresh`,
        {},
        {
          headers: this.headers,
        }
      )
      .then((response) => {
        return response.data;
      });
  }
  async getUserRatings(userId: string) {
    return axios
      .get<Rating[]>(`${RATINGS_URL}/${userId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
  async getTradeRatings(tradeId: string) {
    return axios
      .get<Rating[]>(`${TRADES_URL}/${tradeId}/ratings`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
  async deleteUser(userId: string) {
    return axios
      .delete(`${USERS_URL}/${userId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getMostWanted() {
    return axios
      .get<Beer[]>(`${BEERXCHANGE_BASE_URL}mostWanted`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getMostOffered() {
    return axios
      .get<Beer[]>(`${BEERXCHANGE_BASE_URL}mostOffered`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getRecentOffers() {
    return axios
      .get<Beer[]>(`${BEERXCHANGE_BASE_URL}recentOffers`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  /**
   * A-Z index of the browsable beer directory.
   */
  async getDirectoryIndex() {
    return axios
      .get<DirectoryIndex>(DIRECTORIES_URL, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  /**
   * One page of the beer directory for a letter. The API pages at 50 beers per page.
   */
  async getDirectoryPage(letter: string, page: number) {
    const url =
      page > 1
        ? `${DIRECTORIES_URL}/${encodeURIComponent(letter)}?page=${page}`
        : `${DIRECTORIES_URL}/${encodeURIComponent(letter)}`;
    return axios
      .get<DirectoryPage>(url, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }

  async getPrices(beerUntappdId: string) {
    return axios
      .get<Price[]>(`${PRICES_URL}/${beerUntappdId}`, {
        headers: this.headers,
      })
      .then((response) => {
        return response.data;
      });
  }
}

export default BeerXchangeApi;
